import difference from 'lodash/difference';
import camelCase from 'lodash/camelCase';
import { useTranslation } from 'react-i18next';

import {
  Form,
  Loader,
  PLATFORM_THEME_COLORS,
  SelectInput,
} from '@thinknet-gmbh/platform-common-ui';

import { MainOrSecondaryOccupationEnum, OccupationTypesEnum } from '@thinknet-gmbh/platform-common';

import { useGetAllPlatformJobs } from 'src/hooks/jobs/get-all-platform-jobs.hook';
import { useLazyFindJobLocation } from 'src/hooks/jobs/find-job-location.hook';

import { TitleRowLabel, StyledContainer, StyledRowContainer } from '../../employee-common.styles';

const PositionInformation = () => {
  const { t } = useTranslation(['common', 'employee', 'settings']);

  const { jobs, loading } = useGetAllPlatformJobs();

  const {
    query: findJobLocation,
    jobLocation,
    loading: jobLocationsLoading,
  } = useLazyFindJobLocation();

  const fetchJobLocation = (
    jobId: string,
    companyLocationId: string,
    companyBusinessAddressId: string,
    companyDepartmentId: string
  ) => {
    findJobLocation({
      variables: {
        searchParams: {
          jobId,
          companyLocationId,
          companyBusinessAddressId,
          companyDepartmentId,
        },
      },
    });
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <StyledContainer>
      <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
        {t('employee:workingConditions.labels.position')}
      </TitleRowLabel>
      <StyledRowContainer>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldsValue }) => {
            const currentJobId = getFieldValue('jobId');
            const selectedCompanyId = getFieldValue('companyId');
            const selectedCompanyLocationId = getFieldValue('companyLocationId');
            const selectedCompanyBusinessAddressId = getFieldValue('companyBusinessAddressId');
            const selectedCompanyDepartmentId = getFieldValue('companyDepartmentId');

            const currentJob = jobs.find(({ _id }: any) => _id === currentJobId);

            if (
              currentJob &&
              (currentJob.companyId !== selectedCompanyId ||
                !currentJob.jobLocations.some(
                  ({ companyLocationId, companyBusinessAddressId, companyDepartmentId }: any) =>
                    companyLocationId === selectedCompanyLocationId &&
                    companyBusinessAddressId === selectedCompanyBusinessAddressId &&
                    companyDepartmentId === selectedCompanyDepartmentId
                ))
            ) {
              setFieldsValue({ jobId: null, jobSupervisorId: null });
            }
            return (
              <Form.Item
                name={'jobId'}
                label={t('common:labels.jobTitle')}
                rules={[
                  {
                    required: true,
                    message: t('common:validation.error.jobTitleRequired'),
                  },
                ]}
              >
                <SelectInput placeholder={t('employee:workingConditions.labels.marketingManager')}>
                  {jobs
                    .filter(
                      (job: any) =>
                        job.companyId === selectedCompanyId &&
                        job.jobLocations.some(
                          ({
                            companyLocationId,
                            companyBusinessAddressId,
                            companyDepartmentId,
                          }: any) =>
                            companyLocationId === selectedCompanyLocationId &&
                            companyBusinessAddressId === selectedCompanyBusinessAddressId &&
                            companyDepartmentId === selectedCompanyDepartmentId
                        )
                    )
                    .map((job: any) => (
                      <SelectInput.Option value={job._id} key={job._id}>
                        {job.title}
                      </SelectInput.Option>
                    ))}
                </SelectInput>
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item
          name={'occupationType'}
          label={t('employee:labels.occupationType')}
          rules={[
            {
              required: true,
              message: t('employee:validationError.occupationTypeRequired'),
            },
          ]}
        >
          <SelectInput placeholder={t('employee:workingConditions.labels.permanentEmployment')}>
            {Object.keys(OccupationTypesEnum).map(key => (
              <SelectInput.Option key={key} value={key}>
                {t(`employee:workingConditions.labels.${camelCase(key)}`)}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>
        <Form.Item
          name={'mainOrSecondaryOccupation'}
          label={t('employee:labels.secondaryOccupation')}
          rules={[
            {
              required: true,
              message: t('common:validation.error.fieldRequired'),
            },
          ]}
        >
          <SelectInput placeholder={t('employee:workingConditions.labels.main')}>
            {Object.keys(MainOrSecondaryOccupationEnum).map(key => (
              <SelectInput.Option key={key} value={key}>
                {t(`employee:workingConditions.labels.${key.toLowerCase()}`)}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>
        <Form.Item noStyle dependencies={['jobId']}>
          {({ getFieldValue }) => {
            const jobId = getFieldValue('jobId');
            const companyLocationId = getFieldValue('companyLocationId');
            const companyBusinessAddressId = getFieldValue('companyBusinessAddressId');
            const companyDepartmentId = getFieldValue('companyDepartmentId');

            const formJobLocationData = [
              jobId,
              companyLocationId,
              companyBusinessAddressId,
              companyDepartmentId,
            ];
            const jobLocationData = [
              jobLocation.jobId,
              jobLocation.companyLocationId,
              jobLocation.companyBusinessAddressId,
              jobLocation.companyDepartmentId,
            ];

            const jobSupervisorsToRender = jobId ? jobLocation.jobSupervisors : [];

            if (
              jobId &&
              difference(formJobLocationData, jobLocationData).length &&
              !jobLocationsLoading
            ) {
              fetchJobLocation(
                jobId,
                companyLocationId,
                companyBusinessAddressId,
                companyDepartmentId
              );
            }

            return (
              <Form.Item
                name="jobSupervisorId"
                label={t('employee:labels.supervisor')}
                rules={[
                  {
                    required: true,
                    message: t('employee:validationError.supervisorRequired'),
                  },
                ]}
              >
                <SelectInput placeholder={t('common:placeholders.selectFromList')}>
                  {jobSupervisorsToRender?.map((jobSupervisor: any) => (
                    <SelectInput.Option key={jobSupervisor._id} value={jobSupervisor._id}>
                      {jobSupervisor.supervisorMember
                        ? `${jobSupervisor.supervisorJobTitle} (${jobSupervisor.supervisorMember?.profile?.displayName})`
                        : jobSupervisor.supervisorJobTitle}
                    </SelectInput.Option>
                  ))}
                </SelectInput>
              </Form.Item>
            );
          }}
        </Form.Item>
      </StyledRowContainer>
    </StyledContainer>
  );
};

export default PositionInformation;
